// public/v3/src/lib/scheduling-config-model.js
// Pure model for the provider "Scheduling" tab (pages/scheduling.jsx): the
// shape of `selfSchedulingConfig`, its defaults, the editing helpers the page
// calls, and a client-side mirror of the server's validation so Save is only
// offered for a document the server will accept. Framework-free CommonJS so
// Jest can require it; the JSX imports it through esbuild's CJS interop.
//
// The document (settings key `selfSchedulingConfig`, written only through
// PUT /api/scheduling/config with an optimistic `version`):
//   { version, physicianId, visitTypes:[{id,name,durationMin,mode,description,enabled}],
//     weeklyHours:{mon..sun:[{start:'HH:MM',end:'HH:MM'}]},
//     overrides:[{date:'YYYY-MM-DD', closed:true, note} | {date, windows:[…], note}],
//     rules:{minNoticeHours,maxAdvanceDays,slotGranularityMin,bufferMin,holdTtlMin} }
//
// LIMITS below are the server's rules (plan §1a) verbatim — a change on one
// side is a change on both.

const { parseYmd, addDays, compareYmd, modeLabel } = require('./schedule-picker-model.js');

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri'];
const DAY_LABEL = { mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday' };
const DAY_SHORT = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };
const MODES = ['in_person', 'video'];

const LIMITS = Object.freeze({
  // 0..20: an empty list SAVES (the setup checklist reports it); a patient can
  // only book once one enabled type exists (server isBookable).
  visitTypes: { min: 0, max: 20 },
  name: { min: 1, max: 50 },            // Elation `reason` cap — nothing truncates
  durationMin: { min: 5, max: 240, step: 5 },
  description: { max: 300 },
  windowsPerDay: 4,
  // No lower bound on dates: the server accepts its own prior output as time
  // passes (plan §12 addendum) and the page keeps past rows behind "Show
  // past", where Remove is how the 60-row cap is managed.
  overrides: { max: 60, futureDays: 366, noteMax: 200 },
  minNoticeHours: { min: 0, max: 336 },
  maxAdvanceDays: { min: 1, max: 180 },
  slotGranularityMin: [null, 5, 10, 15, 20, 30, 60],
  bufferMin: { min: 0, max: 60 },
  holdTtlMin: { min: 5, max: 60 },
  jsonChars: 20000,
});

const DEFAULT_RULES = Object.freeze({ minNoticeHours: 24, maxAdvanceDays: 60, slotGranularityMin: null, bufferMin: 0, holdTtlMin: 20 });
const SEED_RANGE = Object.freeze({ start: '09:00', end: '17:00' });

function emptyWeek() {
  const w = {};
  for (const d of DAYS) w[d] = [];
  return w;
}

function DEFAULT_CONFIG() {
  return { version: 0, physicianId: null, visitTypes: [], weeklyHours: emptyWeek(), overrides: [], rules: { ...DEFAULT_RULES } };
}

/* ── times ('HH:MM', 24h) ─────────────────────────────────────────────── */

const HHMM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

function hhmmToMin(s) {
  const m = HHMM_RE.exec(String(s || ''));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

function minToHhmm(min) {
  const n = Math.max(0, Math.min(1439, Math.round(Number(min) || 0)));
  return `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;
}

// '13:15' → '1:15 PM'; '00:00' → '12:00 AM'; anything unparseable → ''.
function formatHhmm12(s) {
  const min = hhmmToMin(s);
  if (min == null) return '';
  const h24 = Math.floor(min / 60);
  const mm = String(min % 60).padStart(2, '0');
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${mm} ${h24 < 12 ? 'AM' : 'PM'}`;
}

// The <select> options for a time: 15-minute steps, 6:00 AM–9:00 PM by
// default (identical on desktop and iPad, keyboard-typeable, always on the
// grid). `include` splices in a saved off-grid value so it is never silently
// changed by the control that shows it.
function timeOptions({ fromMin = 6 * 60, toMin = 21 * 60, stepMin = 15, include = [] } = {}) {
  const values = new Set();
  for (let m = fromMin; m <= toMin; m += stepMin) values.add(minToHhmm(m));
  for (const v of include) if (hhmmToMin(v) != null) values.add(v);
  return Array.from(values)
    .sort((a, b) => hhmmToMin(a) - hhmmToMin(b))
    .map((value) => ({ value, label: formatHhmm12(value) }));
}

// Options strictly after `start` — what the end select may show.
function timeOptionsAfter(start, opts) {
  const s = hhmmToMin(start);
  return timeOptions(opts).filter((o) => s == null || hhmmToMin(o.value) > s);
}

/* ── ranges ({start, end}) ────────────────────────────────────────────── */

function isRangeShape(r) { return !!r && typeof r === 'object' && typeof r.start === 'string' && typeof r.end === 'string'; }

function rangeValid(r) {
  if (!isRangeShape(r)) return false;
  const a = hhmmToMin(r.start); const b = hhmmToMin(r.end);
  return a != null && b != null && a < b;
}

// Strict overlap: touching (12:00 / 12:00) is NOT overlap.
function rangesOverlap(a, b) {
  if (!rangeValid(a) || !rangeValid(b)) return false;
  return hhmmToMin(a.start) < hhmmToMin(b.end) && hhmmToMin(b.start) < hhmmToMin(a.end);
}

// Trim, keep only range-shaped entries, sort by start (invalid starts last).
// For save/fingerprint — the editor keeps the person's row order while editing.
function normalizeRanges(ranges) {
  const out = (Array.isArray(ranges) ? ranges : [])
    .filter(isRangeShape)
    .map((r) => ({ start: r.start.trim(), end: r.end.trim() }));
  const key = (r) => { const m = hhmmToMin(r.start); return m == null ? 100000 : m; };
  return out.sort((a, b) => key(a) - key(b) || (hhmmToMin(a.end) || 0) - (hhmmToMin(b.end) || 0));
}

// [{ index, message }] — index −1 for a day-level problem.
function validateRanges(ranges) {
  const list = Array.isArray(ranges) ? ranges : [];
  const errors = [];
  if (list.length > LIMITS.windowsPerDay) errors.push({ index: -1, message: `At most ${LIMITS.windowsPerDay} time ranges per day` });
  list.forEach((r, i) => {
    if (!isRangeShape(r) || hhmmToMin(r.start) == null || hhmmToMin(r.end) == null) { errors.push({ index: i, message: 'Choose a start and end time' }); return; }
    if (hhmmToMin(r.end) <= hhmmToMin(r.start)) { errors.push({ index: i, message: 'End must be after start' }); return; }
    for (let j = 0; j < i; j++) {
      if (rangesOverlap(list[j], r)) { errors.push({ index: i, message: 'Hours overlap' }); return; }
    }
  });
  return errors;
}

function cloneRanges(ranges) { return normalizeRanges(ranges).map((r) => ({ ...r })); }

function withWeek(config, weeklyHours) { return { ...config, weeklyHours }; }

function setDayClosed(config, day, closed) {
  if (!DAYS.includes(day)) return config;
  const week = { ...config.weeklyHours };
  if (closed) week[day] = [];
  else if (!Array.isArray(week[day]) || week[day].length === 0) week[day] = [{ ...SEED_RANGE }];
  else return config;
  return withWeek(config, week);
}

// Deep clone: editing Tuesday afterwards must not move Monday.
function copyToWeekdays(config, fromDay) {
  if (!DAYS.includes(fromDay)) return config;
  const src = config.weeklyHours[fromDay] || [];
  const week = { ...config.weeklyHours };
  for (const d of WEEKDAYS) week[d] = src.map((r) => ({ ...r }));
  return withWeek(config, week);
}

// A new range starts where the last one ended (or 9:00) and runs up to four
// hours, clipped at 9:00 PM; when the day is already full the config is unchanged.
function addRange(config, day) {
  if (!DAYS.includes(day)) return config;
  const cur = Array.isArray(config.weeklyHours[day]) ? config.weeklyHours[day] : [];
  if (cur.length >= LIMITS.windowsPerDay) return config;
  const lastEnd = cur.reduce((m, r) => Math.max(m, hhmmToMin(r.end) == null ? 0 : hhmmToMin(r.end)), 0);
  const start = cur.length ? lastEnd : hhmmToMin(SEED_RANGE.start);
  if (start >= 21 * 60) return config;
  const end = Math.min(start + 240, 21 * 60);
  const week = { ...config.weeklyHours, [day]: cur.concat([{ start: minToHhmm(start), end: minToHhmm(end) }]) };
  return withWeek(config, week);
}

function updateRange(config, day, index, patch) {
  if (!DAYS.includes(day)) return config;
  const cur = Array.isArray(config.weeklyHours[day]) ? config.weeklyHours[day] : [];
  if (index < 0 || index >= cur.length) return config;
  const next = cur.map((r, i) => (i === index ? { ...r, ...patch } : r));
  return withWeek(config, { ...config.weeklyHours, [day]: next });
}

function removeRange(config, day, index) {
  if (!DAYS.includes(day)) return config;
  const cur = Array.isArray(config.weeklyHours[day]) ? config.weeklyHours[day] : [];
  return withWeek(config, { ...config.weeklyHours, [day]: cur.filter((_, i) => i !== index) });
}

/* ── visit types ─────────────────────────────────────────────────────── */

// A client-minted uuid keys a new visit type in the draft (and in the preview
// payload) until the server has seen it; the server keeps a well-formed id.
function mintId() {
  try {
    if (typeof crypto !== 'undefined' && crypto && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch (_) { /* fall through */ }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
  });
}

function newVisitType(seed = {}) {
  return {
    id: typeof seed.id === 'string' && seed.id ? seed.id : mintId(),
    name: typeof seed.name === 'string' ? seed.name : '',
    durationMin: Number.isFinite(Number(seed.durationMin)) && Number(seed.durationMin) > 0 ? Number(seed.durationMin) : 30,
    mode: MODES.includes(seed.mode) ? seed.mode : 'in_person',
    description: typeof seed.description === 'string' ? seed.description : '',
    enabled: seed.enabled !== false,
  };
}

// { name?, durationMin?, mode?, description? } — empty object when fine.
function validateVisitType(vt) {
  const e = {};
  const name = String((vt && vt.name) || '').trim();
  if (name.length < LIMITS.name.min) e.name = 'Give this visit type a name';
  else if (name.length > LIMITS.name.max) e.name = `Keep the name to ${LIMITS.name.max} characters`;
  const d = Number(vt && vt.durationMin);
  if (!Number.isInteger(d) || d < LIMITS.durationMin.min || d > LIMITS.durationMin.max) e.durationMin = `Length must be ${LIMITS.durationMin.min}–${LIMITS.durationMin.max} minutes`;
  else if (d % LIMITS.durationMin.step !== 0) e.durationMin = `Length must be a multiple of ${LIMITS.durationMin.step} minutes`;
  if (!MODES.includes(vt && vt.mode)) e.mode = 'Choose in person or video';
  const desc = String((vt && vt.description) || '');
  if (desc.length > LIMITS.description.max) e.description = `Keep the description to ${LIMITS.description.max} characters`;
  return e;
}

function upsertVisitType(config, vt) {
  const list = Array.isArray(config.visitTypes) ? config.visitTypes : [];
  const at = list.findIndex((x) => x.id === vt.id);
  const next = at >= 0 ? list.map((x, i) => (i === at ? { ...x, ...vt } : x)) : list.concat([{ ...vt }]);
  return { ...config, visitTypes: next };
}

function removeVisitType(config, id) {
  return { ...config, visitTypes: (config.visitTypes || []).filter((x) => x.id !== id) };
}

// '30 min · In person'
function visitTypeSummary(vt) {
  const parts = [];
  if (vt && Number.isFinite(Number(vt.durationMin))) parts.push(`${Number(vt.durationMin)} min`);
  if (vt && vt.mode) parts.push(modeLabel(vt.mode));
  return parts.join(' · ');
}

/* ── overrides ───────────────────────────────────────────────────────── */

function normalizeOverride(ov) {
  if (!ov || typeof ov !== 'object') return null;
  const date = typeof ov.date === 'string' ? ov.date.trim() : '';
  const closed = ov.closed === true || !Array.isArray(ov.windows);
  const out = { date, closed };
  if (!closed) out.windows = normalizeRanges(ov.windows);
  if (typeof ov.note === 'string' && ov.note.trim()) out.note = ov.note.trim();
  return out;
}

function sortOverrides(overrides) {
  return (Array.isArray(overrides) ? overrides.slice() : []).sort((a, b) => compareYmd(String(a.date || ''), String(b.date || '')));
}

// { date?, note?, windows?: [{index,message}] }
function validateOverride(ov, { todayYmd } = {}) {
  const e = {};
  const date = ov && typeof ov.date === 'string' ? ov.date : '';
  if (ov && typeof ov.note === 'string' && ov.note.length > LIMITS.overrides.noteMax) e.note = `Keep the note to ${LIMITS.overrides.noteMax} characters`;
  if (!parseYmd(date)) e.date = 'Choose a date';
  else if (todayYmd) {
    const hi = addDays(todayYmd, LIMITS.overrides.futureDays);
    if (compareYmd(date, hi) > 0) e.date = 'Dates can be set up to a year ahead';
  }
  if (ov && ov.closed !== true) {
    const windows = Array.isArray(ov && ov.windows) ? ov.windows : [];
    if (windows.length === 0) e.windows = [{ index: -1, message: 'Add the hours for this day, or mark it closed' }];
    else {
      const errs = validateRanges(windows);
      if (errs.length) e.windows = errs;
    }
  }
  return e;
}

// One override per date: a second one for the same day replaces the first.
function upsertOverride(config, ov) {
  const list = Array.isArray(config.overrides) ? config.overrides : [];
  const at = list.findIndex((x) => x.date === ov.date);
  const next = at >= 0 ? list.map((x, i) => (i === at ? { ...ov } : x)) : list.concat([{ ...ov }]);
  return { ...config, overrides: sortOverrides(next) };
}

function removeOverride(config, date) {
  return { ...config, overrides: (config.overrides || []).filter((x) => x.date !== date) };
}

// { upcoming, past } — past stays behind "Show past", never deleted for the person.
function splitPastOverrides(overrides, todayYmd) {
  const sorted = sortOverrides(overrides);
  const upcoming = []; const past = [];
  for (const ov of sorted) (compareYmd(String(ov.date || ''), todayYmd) < 0 ? past : upcoming).push(ov);
  return { upcoming, past };
}

/* ── the whole document ──────────────────────────────────────────────── */

function normalizePhysicianId(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : null;
}

function intOr(v, fallback) {
  const n = Number(v);
  return Number.isInteger(n) ? n : fallback;
}

function normalizeConfig(raw) {
  const base = DEFAULT_CONFIG();
  const src = raw && typeof raw === 'object' ? raw : {};
  const week = emptyWeek();
  const srcWeek = src.weeklyHours && typeof src.weeklyHours === 'object' ? src.weeklyHours : {};
  for (const d of DAYS) week[d] = normalizeRanges(srcWeek[d]);
  const rules = { ...base.rules };
  const srcRules = src.rules && typeof src.rules === 'object' ? src.rules : {};
  rules.minNoticeHours = intOr(srcRules.minNoticeHours, base.rules.minNoticeHours);
  rules.maxAdvanceDays = intOr(srcRules.maxAdvanceDays, base.rules.maxAdvanceDays);
  rules.slotGranularityMin = srcRules.slotGranularityMin == null ? null : intOr(srcRules.slotGranularityMin, null);
  rules.bufferMin = intOr(srcRules.bufferMin, base.rules.bufferMin);
  rules.holdTtlMin = intOr(srcRules.holdTtlMin, base.rules.holdTtlMin);
  return {
    version: Math.max(0, intOr(src.version, 0)),
    physicianId: normalizePhysicianId(src.physicianId),
    visitTypes: (Array.isArray(src.visitTypes) ? src.visitTypes : [])
      .filter((v) => v && typeof v === 'object')
      .map((v) => ({
        id: typeof v.id === 'string' && v.id ? v.id : mintId(),
        name: String(v.name || '').trim(),
        durationMin: intOr(v.durationMin, 0),
        // A string mode is kept as-is (validateVisitType reports a bad one, as
        // the server would); only a missing mode gets the default.
        mode: typeof v.mode === 'string' ? v.mode : 'in_person',
        description: String(v.description || '').trim(),
        enabled: v.enabled !== false,
      })),
    weeklyHours: week,
    overrides: sortOverrides((Array.isArray(src.overrides) ? src.overrides : []).map(normalizeOverride).filter(Boolean)),
    rules,
  };
}

// { ok, errors: [{ path, message }] } — the server's rules, so Save is offered
// only for a document it will accept. Paths follow the server's shape.
function validateConfig(config, { todayYmd } = {}) {
  const c = normalizeConfig(config);
  const errors = [];
  const push = (path, message) => errors.push({ path, message });

  // No physician and no visit types are both SAVEABLE (the server accepts
  // them; the setup checklist is what reports them) — a practice can set up in
  // any order and save as it goes. A physician id, when present, is a positive
  // integer; normalizeConfig has already turned anything else into null.

  if (c.visitTypes.length > LIMITS.visitTypes.max) push('visitTypes', `At most ${LIMITS.visitTypes.max} visit types`);
  const seenIds = new Set();
  c.visitTypes.forEach((vt, i) => {
    const e = validateVisitType(vt);
    for (const k of Object.keys(e)) push(`visitTypes[${i}].${k}`, e[k]);
    if (seenIds.has(vt.id)) push(`visitTypes[${i}].id`, 'Duplicate visit type id');
    seenIds.add(vt.id);
  });

  for (const d of DAYS) {
    for (const e of validateRanges(c.weeklyHours[d])) {
      push(e.index < 0 ? `weeklyHours.${d}` : `weeklyHours.${d}[${e.index}]`, `${DAY_LABEL[d]}: ${e.message}`);
    }
  }

  if (c.overrides.length > LIMITS.overrides.max) push('overrides', `At most ${LIMITS.overrides.max} date overrides`);
  const seenDates = new Set();
  c.overrides.forEach((ov, i) => {
    const e = validateOverride(ov, { todayYmd });
    if (e.date) push(`overrides[${i}].date`, e.date);
    if (e.note) push(`overrides[${i}].note`, `${ov.date || 'Override'}: ${e.note}`);
    if (e.windows) for (const w of e.windows) push(w.index < 0 ? `overrides[${i}].windows` : `overrides[${i}].windows[${w.index}]`, `${ov.date || 'Override'}: ${w.message}`);
    if (seenDates.has(ov.date)) push(`overrides[${i}].date`, 'Two overrides on the same date');
    seenDates.add(ov.date);
  });

  const r = c.rules;
  const between = (v, lim) => Number.isInteger(v) && v >= lim.min && v <= lim.max;
  if (!between(r.minNoticeHours, LIMITS.minNoticeHours)) push('rules.minNoticeHours', `Minimum notice must be ${LIMITS.minNoticeHours.min}–${LIMITS.minNoticeHours.max} hours`);
  if (!between(r.maxAdvanceDays, LIMITS.maxAdvanceDays)) push('rules.maxAdvanceDays', `Booking window must be ${LIMITS.maxAdvanceDays.min}–${LIMITS.maxAdvanceDays.max} days`);
  if (!LIMITS.slotGranularityMin.includes(r.slotGranularityMin)) push('rules.slotGranularityMin', 'Slot spacing must match the visit length or be 5, 10, 15, 20, 30 or 60 minutes');
  if (!between(r.bufferMin, LIMITS.bufferMin)) push('rules.bufferMin', `Buffer must be ${LIMITS.bufferMin.min}–${LIMITS.bufferMin.max} minutes`);
  if (!between(r.holdTtlMin, LIMITS.holdTtlMin)) push('rules.holdTtlMin', `Hold time must be ${LIMITS.holdTtlMin.min}–${LIMITS.holdTtlMin.max} minutes`);
  // A notice longer than the horizon means nothing could ever be booked.
  if (Number.isInteger(r.minNoticeHours) && Number.isInteger(r.maxAdvanceDays) && r.minNoticeHours / 24 > r.maxAdvanceDays) {
    push('rules.minNoticeHours', 'Minimum notice is longer than the booking window — nothing could ever be booked');
  }

  if (JSON.stringify(c).length > LIMITS.jsonChars) push('config', 'This configuration is too large to save — shorten descriptions or remove overrides');

  return { ok: errors.length === 0, errors };
}

// Stable text for dirty tracking: order-independent (visit types by id,
// overrides by date, ranges by start, object keys sorted) and blind to
// `version`, which the server bumps on its own.
function stableStringify(v) {
  if (Array.isArray(v)) return `[${v.map(stableStringify).join(',')}]`;
  if (v && typeof v === 'object') {
    return `{${Object.keys(v).sort().map((k) => `${JSON.stringify(k)}:${stableStringify(v[k])}`).join(',')}}`;
  }
  return JSON.stringify(v === undefined ? null : v);
}

function configFingerprint(config) {
  const c = normalizeConfig(config);
  const { version, ...rest } = c;
  rest.visitTypes = rest.visitTypes.slice().sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return stableStringify(rest);
}

// After a successful PUT. The person may have kept editing while the request
// was in flight; those edits are never discarded. Unchanged since it was
// sent → the saved document is the draft. Changed → the draft keeps its
// edits at the new version, so the bar stays and the next Save cannot 409
// against ourselves.
function reconcileAfterSave(currentDraft, sentConfig, savedConfig) {
  if (!currentDraft || configFingerprint(currentDraft) === configFingerprint(sentConfig)) return savedConfig;
  return { ...currentDraft, version: savedConfig.version };
}

// What the calendar still needs before a patient can book: mirrors the
// server's isBookable (physician + hours on ≥1 day + ≥1 enabled visit type).
function setupChecklist(config) {
  const c = normalizeConfig(config);
  const physician = !!c.physicianId;
  const hours = DAYS.some((d) => c.weeklyHours[d].some(rangeValid));
  const visitType = c.visitTypes.some((vt) => vt.enabled && Object.keys(validateVisitType(vt)).length === 0);
  return { physician, hours, visitType, complete: physician && hours && visitType };
}

// Rows for the "Next 7 days" preview: per day, the total and one stacked bar
// segment per visit type, widths relative to the busiest day in the window.
//   days: [{ date, counts: { <visitTypeId>: n } }]  (POST /api/scheduling/preview)
function previewBars(days, visitTypes) {
  const types = Array.isArray(visitTypes) ? visitTypes : [];
  const list = Array.isArray(days) ? days : [];
  const rows = list.map((d) => {
    const counts = d && d.counts && typeof d.counts === 'object' ? d.counts : {};
    const segments = types.map((vt) => ({ id: vt.id, name: vt.name, count: Math.max(0, intOr(counts[vt.id], 0)) }));
    // Counts for ids the page does not know (a type removed in the draft) are still openings.
    const known = new Set(types.map((t) => t.id));
    for (const k of Object.keys(counts)) if (!known.has(k)) segments.push({ id: k, name: 'Other', count: Math.max(0, intOr(counts[k], 0)) });
    const total = segments.reduce((s, x) => s + x.count, 0);
    return { date: d ? d.date : '', total, segments };
  });
  const maxTotal = rows.reduce((m, r) => Math.max(m, r.total), 0);
  for (const r of rows) {
    for (const s of r.segments) s.pct = maxTotal > 0 ? Math.round((s.count / maxTotal) * 1000) / 10 : 0;
  }
  return { rows, maxTotal };
}

/* ── the rules card's option lists ───────────────────────────────────── */

const MIN_NOTICE_OPTIONS = [
  { value: 0, label: 'No minimum' }, { value: 2, label: '2 hours' }, { value: 4, label: '4 hours' },
  { value: 12, label: '12 hours' }, { value: 24, label: '1 day' }, { value: 48, label: '2 days' },
  { value: 72, label: '3 days' }, { value: 168, label: '1 week' },
];
const MAX_ADVANCE_OPTIONS = [
  { value: 14, label: '2 weeks' }, { value: 21, label: '3 weeks' }, { value: 28, label: '4 weeks' },
  { value: 42, label: '6 weeks' }, { value: 60, label: '2 months (60 days)' }, { value: 90, label: '3 months (90 days)' },
  { value: 120, label: '4 months (120 days)' }, { value: 180, label: '6 months (180 days)' },
];
const GRANULARITY_OPTIONS = [
  { value: null, label: 'Match visit length' }, { value: 10, label: 'Every 10 minutes' }, { value: 15, label: 'Every 15 minutes' },
  { value: 20, label: 'Every 20 minutes' }, { value: 30, label: 'Every 30 minutes' }, { value: 60, label: 'Every hour' },
];
const BUFFER_OPTIONS = [
  { value: 0, label: 'None' }, { value: 5, label: '5 minutes' }, { value: 10, label: '10 minutes' },
  { value: 15, label: '15 minutes' }, { value: 20, label: '20 minutes' }, { value: 30, label: '30 minutes' },
];
const HOLD_OPTIONS = [
  { value: 10, label: '10 minutes' }, { value: 15, label: '15 minutes' }, { value: 20, label: '20 minutes' },
  { value: 30, label: '30 minutes' }, { value: 45, label: '45 minutes' }, { value: 60, label: '1 hour' },
];

// A saved value the list does not carry is shown rather than silently moved.
function withCurrentOption(options, value, labelFor) {
  if (options.some((o) => o.value === value)) return options;
  const label = typeof labelFor === 'function' ? labelFor(value) : String(value);
  return options.concat([{ value, label }]).sort((a, b) => (a.value == null ? -1 : b.value == null ? 1 : a.value - b.value));
}

module.exports = {
  DAYS, WEEKDAYS, DAY_LABEL, DAY_SHORT, MODES, LIMITS, DEFAULT_RULES, SEED_RANGE,
  DEFAULT_CONFIG,
  hhmmToMin, minToHhmm, formatHhmm12, timeOptions, timeOptionsAfter,
  rangeValid, rangesOverlap, normalizeRanges, validateRanges, cloneRanges,
  setDayClosed, copyToWeekdays, addRange, updateRange, removeRange,
  mintId, newVisitType, validateVisitType, upsertVisitType, removeVisitType, visitTypeSummary,
  normalizeOverride, sortOverrides, validateOverride, upsertOverride, removeOverride, splitPastOverrides,
  normalizePhysicianId, normalizeConfig, validateConfig, configFingerprint, reconcileAfterSave, setupChecklist, previewBars,
  MIN_NOTICE_OPTIONS, MAX_ADVANCE_OPTIONS, GRANULARITY_OPTIONS, BUFFER_OPTIONS, HOLD_OPTIONS, withCurrentOption,
};
